import { useEffect, useState } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { useFinePointer } from "@/hooks/useFinePointer";
import styles from "./Cursor.module.css";

/**
 * A thin emerald ring that trails the pointer and opens around anything that can
 * be clicked. The system cursor stays; this only accompanies it.
 */
export function Cursor() {
  const fine = useFinePointer();
  const reduce = useReducedMotion();
  if (!fine || reduce) return null;
  return <Ring />;
}

function Ring() {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const spring = { stiffness: 420, damping: 36, mass: 0.5 };
  const sx = useSpring(x, spring);
  const sy = useSpring(y, spring);
  const [over, setOver] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setShown(true);
      const target = e.target instanceof Element ? e.target : null;
      setOver(!!target?.closest("a, button, [role='button'], summary, label"));
    };
    const leave = () => setShown(false);
    window.addEventListener("pointermove", move, { passive: true });
    document.documentElement.addEventListener("pointerleave", leave);
    return () => {
      window.removeEventListener("pointermove", move);
      document.documentElement.removeEventListener("pointerleave", leave);
    };
  }, [x, y]);

  return (
    <motion.div className={styles.cursor} style={{ x: sx, y: sy }} aria-hidden="true">
      <motion.span
        className={styles.ring}
        animate={{ scale: over ? 1.9 : 1, opacity: shown ? (over ? 1 : 0.7) : 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      />
    </motion.div>
  );
}
